import { getBusinessPlan } from './businessPlans.js';

export const localProducts = [
  {
    "slug": "free",
    "planId": "free",
    "eyebrow": "Free forever · Website only",
    "headline": "Your business website. Free forever.",
    "intro": "A website for your business, with automated onboarding and self-service wiki support. Use it as your main website or assess a complementary site alongside the one you have. Free is website only: the ongoing marketing system is a separate upgrade.",
    "fit": "For local service businesses, coaches and consultants who need a clear online home without a monthly website subscription.",
    "outcome": "A business website. No ongoing marketing system.",
    "art": "feedbackToAction",
    "audiences": [
      [
        "Home service businesses",
        "Give customers a clear place to understand your services and contact your business."
      ],
      [
        "Coaches",
        "Explain the work you offer using accurate details and approved imagery."
      ],
      [
        "Independent consultants",
        "Give prospective clients a useful introduction to your expertise and services."
      ]
    ],
    "workflow": [
      [
        "Provide your business details",
        "Complete website onboarding with accurate services, contact details and approved images."
      ],
      [
        "Review your website",
        "Check the preview and confirm the facts before launch."
      ],
      [
        "Keep it useful",
        "Use the wiki to maintain your website. Upgrade separately if you want ongoing marketing tools or automation."
      ]
    ],
    "faqs": [
      [
        "Is this a website or a marketing system?",
        "Free is website only. It includes automated website onboarding, wiki support and RepUpgrade attribution. It does not include ongoing AI review replies, articles, social publishing, marketing automations or team chat."
      ],
      [
        "Do I have to subscribe later?",
        "No paid marketing subscription is required for the free website. Confirm supported hosting, domain connection and any third-party costs before setup."
      ],
      [
        "What if I already have a website?",
        "You can keep it. Assess a complementary site or supported subdomain rather than replacing a website that already works for you."
      ],
      [
        "What support is included?",
        "The Free website uses self-service wiki support. Guided setup and strategy support are available through the paid $299 initiation service; confirm scope and service period before purchase."
      ]
    ],
    "next": "Choose Grow for tools you operate, Amplify for an automated marketing system and AI-team chat, or Complete for the full marketing flywheel."
  },
  {
    "slug": "grow",
    "planId": "grow",
    "eyebrow": "Build a steady foundation",
    "headline": "Your next customer needs a reason to trust you.",
    "intro": "You do the work worth talking about. Your website is included. Grow helps you collect the feedback, respond with care, and turn one useful customer theme into an article and a social post each month. Human-led AI, grounded in your business.",
    "fit": "For solo operators and small businesses that want a consistent starting rhythm without a full content schedule.",
    "outcome": "Stay present without starting from a blank page every month.",
    "art": "reviewsExplained",
    "audiences": [
      [
        "Home service businesses",
        "A practical fit for a solo cleaner, handyman, or lawn-care operator getting a regular review and content habit in place."
      ],
      [
        "Coaches",
        "Keep one useful monthly idea connected to the questions clients ask, with room for thoughtful review replies."
      ],
      [
        "Independent consultants",
        "Explain one important service question each month and give prospective clients something useful to read."
      ]
    ],
    "workflow": [
      [
        "Read your starting point",
        "Request your report, then decide which service and customer questions deserve the first month’s attention."
      ],
      [
        "Bring your business context",
        "Connect supported profiles, set your voice, and confirm the facts the agents should use."
      ],
      [
        "Keep a monthly rhythm",
        "Ruby supports your replies. Mabel drafts your article. Annie helps carry the message to a supported social channel. Review before publishing."
      ]
    ],
    "faqs": [
      [
        "What does Grow add to Free?",
        "Customer-operated review and content tools, monthly allowances, widgets and Wall of Love. Grow does not include marketing automations, video testimonials or Rep-Upgrade.ai team chat."
      ],
      [
        "Is Google Business Profile optimization included in $99?",
        "Grow is available without GBP optimization: seven days free, then $99/month. You can add the one-time $299 optimization service later through your client portal."
      ],
      [
        "Does Grow include WhatsApp or LinkedIn?",
        "No. Grow currently includes 1,000 emails and 100 SMS per month. Facebook, Instagram, and TikTok are enabled social channels; LinkedIn and WhatsApp are not included. The one-post monthly allowance does not increase with the number of connected profiles."
      ],
      [
        "Is a website included?",
        "Yes. Every plan includes a business website. It can replace a basic service site or complement your current website, subject to supported domain setup. Domain registration and third-party costs are separate."
      ],
      [
        "What do I need to provide?",
        "Bring access to your Google account and supported social profiles to your setup appointment. Joe sits with you while you connect them and handles the configuration. Confirm your business facts and permissions; keep your passwords private."
      ],
      [
        "Who should choose a larger plan?",
        "If you need more than ten replies or one article and one social post per month, compare Amplify. Complete adds the full strategy and a more frequent content rhythm."
      ],
      [
        "Does Grow automate my marketing?",
        "No. You initiate the review and content work. Marketing automations and Rep-Upgrade.ai team chat begin with Amplify."
      ]
    ],
    "next": "Move to Amplify when a single monthly article and post no longer match the activity of your business."
  },
  {
    "slug": "amplify",
    "planId": "amplify",
    "eyebrow": "Give good work more visibility",
    "headline": "Your expertise deserves more than an occasional post.",
    "intro": "Four useful articles. Four social posts. Fifty thoughtful review replies. Amplify turns your services, customer questions, and approved feedback into a steady content rhythm—with your voice and human judgment at the center.",
    "fit": "For established businesses with more customer activity, several services to explain, and enough expertise to share regularly.",
    "outcome": "Give prospective customers more reasons to understand your approach.",
    "art": "reputationOverview",
    "audiences": [
      [
        "Home service businesses",
        "Give a cleaning crew, landscaper, painter, or pest service more space to explain services and answer seasonal questions."
      ],
      [
        "Coaches",
        "Develop recurring questions into useful articles and social content without exposing private client conversations."
      ],
      [
        "Independent consultants",
        "Explain your process, common decisions, and service differences in a more consistent content schedule."
      ]
    ],
    "workflow": [
      [
        "Choose the themes",
        "Use your report, actual services, and recurring customer questions to choose useful directions for the month."
      ],
      [
        "Build the content rhythm",
        "Mabel drafts four articles with covers; Annie supports four social posts. Ruby helps with up to fifty review replies in the month."
      ],
      [
        "Review and refine",
        "Check the facts and your voice. Use what customers ask next to shape the next round of useful content."
      ]
    ],
    "faqs": [
      [
        "How is Amplify different from Grow?",
        "Both include a website. Amplify adds configured marketing automations, Rep-Upgrade.ai team chat, suggestions and execution, plus fifty review replies, four articles and four social posts per month. Grow is operated by you without marketing automations or video testimonials."
      ],
      [
        "How does the Amplify trial work?",
        "Purchase Google Business Profile optimization for $299 upfront to receive the two-month free Amplify subscription trial. After the trial, Amplify is $199/month."
      ],
      [
        "Which messaging and social channels are included?",
        "Amplify includes 5,000 emails and 200 SMS per month. Facebook, Instagram, and TikTok are enabled social channels. WhatsApp and LinkedIn are not included in this setup. The four-post allowance is not multiplied by the number of connected profiles."
      ],
      [
        "Do the articles have to be about reviews?",
        "Customer feedback is one useful source. Your real services, recurring questions, process, and approved expertise also help shape relevant article ideas. Check facts and permissions before publishing."
      ],
      [
        "Is a website included?",
        "Yes. Every plan includes a business website. It can replace a basic service site or complement your current website, subject to supported domain setup. Domain registration and third-party costs are separate."
      ],
      [
        "Does Amplify include the full strategy?",
        "You can see how your business appears, but the full strategy belongs to Complete. Amplify focuses on a more frequent reply and content rhythm."
      ]
    ],
    "next": "Choose Complete for the full connected marketing flywheel and a more frequent content rhythm. Your website is included in either plan."
  },
  {
    "slug": "complete",
    "planId": "dominate",
    "eyebrow": "Put the whole system to work",
    "headline": "Your website. Your reputation. Your AI team.",
    "intro": "A one-page website plus blog, built from your Google Business Profile. Bring your domain and we connect it, or keep your existing website and add our site on a subdomain. Complete brings your website, business-data chat, reviews, articles, and social content together for $299/month.",
    "fit": "For businesses ready to make reputation and useful content an ongoing operating rhythm, with human judgment guiding the system.",
    "outcome": "One connected home for your services, customer proof, and useful content.",
    "art": "repupgradeDesktop",
    "audiences": [
      [
        "Home service businesses",
        "A fit for plumbing, HVAC, tree-care, roofing, and other teams with several service questions and a steady stream of customer feedback."
      ],
      [
        "Coaches",
        "Connect your approved method, client feedback, and educational themes into a fuller content rhythm."
      ],
      [
        "Independent consultants",
        "Support a broader library of useful expertise while keeping service claims and recommendations grounded in your actual work."
      ]
    ],
    "workflow": [
      [
        "Establish the direction",
        "Start with your report and business context. RepUpgrade.ai helps interpret available signals and supports the full strategy."
      ],
      [
        "Connect the work",
        "Ruby supports review replies, Mabel drafts an article with a cover every three days, and Annie supports ten social posts per month."
      ],
      [
        "Keep human judgment in charge",
        "Review facts, permissions, sensitive feedback, and your voice. Publish through supported connected accounts and your approval settings."
      ]
    ],
    "faqs": [
      [
        "What makes Complete different?",
        "Every plan includes a website. Complete connects the full marketing strategy, Rep-Upgrade.ai team chat and execution, an article with a cover every three days, ten social posts per month, and the largest sending allowances. Review replies are paced at a maximum of five per day within included usage."
      ],
      [
        "Is Google Business Profile optimization included?",
        "You purchase GBP optimization for $299 upfront, and the first three months of Complete are included. The six-month commitment includes those three months. Subscription billing begins in month four at $299/month for months four, five, and six: $1,196 total across six months before applicable tax. Then $299/month."
      ],
      [
        "Which sending and social channels are included?",
        "Complete includes 10,000 emails and 500 SMS per month. Facebook, Instagram, and TikTok are enabled social channels. WhatsApp and LinkedIn are not included in this setup. Connecting several profiles does not multiply the ten-post monthly allowance."
      ],
      [
        "Can I connect my own sending tools?",
        "Yes. Complete enables your own SMTP email-server connection and your own Twilio-number connection. Provider account access and configuration are required. Confirm any provider charges before enabling those connections."
      ],
      [
        "What website is included?",
        "We build a one-page website plus blog using your Google Business Profile as the starting point. You approve your services, contact details, images, and content before launch. You provide the domain."
      ],
      [
        "What if I already have a website?",
        "Keep it. We can connect your included site and blog on a subdomain such as insights.yourbusiness.com. If you do not have a website, we can connect your domain directly to the included site. Domain access and setup are required."
      ],
      [
        "How does the website support SEO?",
        "Clear service information, a connected blog, and useful articles create more content for customers and search engines to discover. Results depend on your market, content quality, indexing, and website setup; adding a subdomain does not automatically increase rankings on your existing site."
      ],
      [
        "Can I chat with my own business information?",
        "Yes. Complete and Amplify both include chat with your own business content and available connected data. Every plan includes a website; Amplify and Complete add Rep-Upgrade.ai team chat and execution within plan limits."
      ],
      [
        "Does human-led mean a meeting for every article?",
        "Joe meets with you for the initial setup and helps you connect Google and supported social profiles. He handles configuration. Ongoing work follows your agreed scope and approval settings; a separate meeting is not needed for every article."
      ],
      [
        "Do you support ecommerce product reviews?",
        "We assess your store, product-review sources and supported integrations during setup. Complete does not include building or migrating an ecommerce storefront."
      ],
      [
        "Is annual billing available?",
        "Yes. Complete is $3,149/year: save $439, about 12%, compared with twelve $299 monthly payments. Confirm initiation, introductory-offer treatment and the first annual billing date during guided activation."
      ]
    ],
    "next": "Compare the rhythms if you need a smaller starting point. The right plan fits the work you will actually use."
  }
];

export function getLocalProduct(slug) {
 const product = localProducts.find(item => item.slug === slug);
 return product ? { ...product, plan: getBusinessPlan(product.planId) } : null;
}
export function getLocalPlanPath(planId) {
 const product = localProducts.find(item => item.planId === planId);
 return product ? '/plans/' + product.slug : '/pricing#business-plans';
}